import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SITE, absoluteUrl, findRoute, normalisePath } from '@/config/seo';

export function SeoHead() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const path = normalisePath(pathname);
    const route = findRoute(path);
    const locale = i18n.language === 'fr' ? 'fr_FR' : 'en_US';

    const pageTitle = route?.title ?? 'CampusOne';
    const description = route?.description ?? SITE.tagline;

    document.title = pageTitle;
    document.documentElement.lang = i18n.language;

    const meta = (attr: 'name' | 'property', key: string, content: string) => {
      const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    meta('name', 'description', description);
    meta('property', 'og:title', pageTitle);
    meta('property', 'og:description', description);
    meta('property', 'og:url', absoluteUrl(path));
    meta('property', 'og:locale', locale);
    meta('property', 'og:locale:alternate', locale === 'fr_FR' ? 'en_US' : 'fr_FR');
    meta('name', 'twitter:title', pageTitle);
    meta('name', 'twitter:description', description);

    // Authenticated pages should not be indexed.
    meta('name', 'robots', route?.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = absoluteUrl(path);

    // hreflang alternates — only for known routes.
    document.head.querySelectorAll('link[rel="alternate"][data-seo="alt"]').forEach((el) => el.remove());

    if (route) {
      const addLink = (hreflang: string, href: string) => {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = hreflang;
        link.href = href;
        link.dataset.seo = 'alt';
        document.head.appendChild(link);
      };
      addLink('en', absoluteUrl(path));
      addLink('fr', absoluteUrl(path, 'fr'));
      addLink('x-default', absoluteUrl(path));
    }
  }, [pathname, i18n.language]);

  return null;
}
